import {useFormikContext} from 'formik'
import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useLocation, useNavigate} from 'react-router-dom'

import {ServiceItem} from '../../api/booking/types'
import {Class} from '../../api/class/types'
import {isNilOrEmpty} from '../../utils/logic'
import {useAuthState} from '../auth/hooks'
import {basicActions} from './actions'
import {CreateClassBookingFormType} from './forms/create-class-booking-form'
import {CreateServiceBookingFormType} from './forms/create-service-booking-form'
import {basicSelectors} from './selectors'

const getEntityFuncMap: Record<string, (values: CreateClassBookingFormType | CreateServiceBookingFormType) => Class | ServiceItem | null> = {
  class: (values) => (values as CreateClassBookingFormType).class,
  space: (values) => (values as CreateServiceBookingFormType).service,
  service: (values) => (values as CreateServiceBookingFormType).service,
  teacher: (values) => (values as CreateServiceBookingFormType).service,
}

const checkHasPermissions = (values: CreateClassBookingFormType | CreateServiceBookingFormType) => {
  const entity: Class | ServiceItem | null = getEntityFuncMap[values.bookingType](values)
  return entity?.permissionData?.validated === false
}

export const useHomeRedirection = () => {
  const navigate = useNavigate()
  const {pathname} = useLocation()
  const {isLoggedIn} = useAuthState()
  const [isNeedRedirectionByPermission, setIsNeedRedirectionByPermission] = useState(false)
  const [prevIsLoggedIn, setPrevIsLoggedIn] = useState(isLoggedIn)
  const {values} = useFormikContext<
    | CreateClassBookingFormType
    | CreateServiceBookingFormType
  >()

  useEffect(() => {
    setPrevIsLoggedIn(isLoggedIn)
    if (!(!prevIsLoggedIn && isLoggedIn)) {
      return
    }
    setIsNeedRedirectionByPermission(checkHasPermissions(values))
  }, [isLoggedIn])

  const bookingType = values.bookingType
  const pathArr = pathname.split('/').filter((el) => el !== '')
  const currentStep = pathArr[pathArr.length - 1]
  const isNeedRedirection =
    isNeedRedirectionByPermission ||
    (
      ((bookingType === 'service' || bookingType === 'space') &&
        isNilOrEmpty(values.service) &&
        currentStep !== 'select-playspace') ||
      (bookingType === 'teacher' && isNilOrEmpty(values.teacher) && isNilOrEmpty(values.service)) ||
      (bookingType === 'class' && values.sessionId === null
        && currentStep !== 'select-session'
        && !(currentStep === 'selected-sessions' && (values as any).selectedSessions?.length > 0)
        && !((values as any).selectedSessions?.length > 0 && ['set-participants', 'summary', 'set-payment-method'].includes(currentStep))
      )
    )
  if (!isNeedRedirection) {
    return false
  }
  navigate(`/${pathArr[0]}/`)
  return true
}

export const useBookingLoader = () => {
  // TODO: refactor to use local loader with query
  const dispatch = useDispatch()
  const isLoading = useSelector(basicSelectors.isLoading())
  const setIsLoading = (arg: boolean) => dispatch(basicActions.isLoading(arg))
  return {
    isLoading,
    setIsLoading,
  }
}
